import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FilterEnumsService {
  
  constructor() {
    // Service constructor
  }

  /**
   * Get asset type options for admin activities page
   */
  getAssetTypeOptions(): string[] {
    return ['Dataset', 'Model', 'Use Case'];
  }

  /**
   * Get asset type options for consumer/provider/org-admin activities page (no Usecase)
   */
  getNonAdminAssetTypeOptions(): string[] {
    return ['Dataset', 'Model'];
  }

  /**
   * Get asset type options for user activities page (no Use Case).
   */
  getUserActivitiesAssetTypeOptions(): string[] {
    return ['Dataset', 'Model'];
  }

  /**
   * Compute tab activity filter options (My Activities, User Activities, etc.).
   */
  getComputeActivityFilterOptions(): string[] {
    return ['Create', 'Cancel', 'Extend', 'Reset', 'Terminate'];
  }

  /**
   * Get activity options (sorted alphabetically)
   */
  getActivityOptions(): string[] {
    // Keep dropdown values aligned with backend `action` query param.
    return [
      'View',
      'Upload',
      'Create',
      'Update',
      'Delete',
      'Download Access Granted',
      'Download Access Withdrawn',
      'Download Access Rejected',
      'Download Access Requested'
    ];
  }

  /**
   * Platform Admin → My Activities → Assets tab activity filter options.
   * Excludes Upload, Create, Update and download-access actions.
   * Platform Admins already have access to all restricted datasets, so
   * "Download Access Requested" is not shown.
   */
  getAdminAssetActivityOptions(): string[] {
    return [
      'View',
      'Delete',
    ];
  }

  /**
   * Platform Admin → User Activities → Assets tab activity filter options.
   */
  getUserActivitiesAssetActivityOptions(): string[] {
    return this.getActivityOptions();
  }

  /**
   * Resolve an activity label to the canonical filter dropdown spelling when known.
   */
  normalizeActivityFilterLabel(activity: string): string {
    const trimmed = activity?.trim();
    if (!trimmed) return trimmed;
    const canonical = this.getActivityOptions().find(
      (option) => option.toLowerCase() === trimmed.toLowerCase(),
    );
    return canonical ?? trimmed;
  }

  /**
   * Whether an activity label should appear in Activity filter dropdowns.
   * Exact "Download" is excluded; "Download Access *" options remain.
   */
  isActivityFilterOptionAllowed(activity: string): boolean {
    return (activity?.trim().toLowerCase() ?? '') !== 'download';
  }

  /**
   * Provider → My Activities → Assets tab activity filter options.
   */
  getProviderActivityOptions(): string[] {
    return this.getActivityOptions();
  }

  /**
   * Consumer → My Activities → Assets tab activity filter options.
   * Consumers view and manage their own download-access requests only.
   */
  getConsumerActivityOptions(): string[] {
    return [
      'View',
      'Download Access Requested',
      'Download Access Withdrawn',
    ];
  }

  /**
   * Map display asset type to API format
   */
  mapAssetTypeToApi(displayType: string): string {
    const mapping: Record<string, string> = {
      'Dataset': 'DATABANK',
      'Model': 'AI_MODEL',
      'Use Case': 'USECASE',
      'Usecase': 'USECASE'
    };
    return mapping[displayType] || displayType.toUpperCase();
  }

  /**
   * Map display activity to API format
   */
  mapActivityToApi(displayActivity: string): string {
    // Activities are already in the correct format, just uppercase
    return displayActivity.toUpperCase();
  }

  /**
   * Check if asset type should be excluded
   */
  isExcludedAssetType(type: string): boolean {
    const excludedTypes = ['Challenge', 'Data bank', 'Org request', 'Usecase'];
    return excludedTypes.includes(type);
  }
}